import { useEffect, useState } from "react";
import { Search, Plus, Building2, Mail, Phone, KeyRound, Pencil, Loader2, ChevronRight, Copy, CheckCircle2, UserCheck } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import ClientFormDialog from "./ClientFormDialog";

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATUS_LABEL = { brouillon: "Brouillon", envoye: "Envoyé", signe: "Signé", paye: "Payé", impaye: "Impayé", en_retard: "En retard", annule: "Annulé" };

function AccountBadge({ status }) {
  return status === "active"
    ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-400 flex items-center gap-1" data-testid="client-account-active"><UserCheck className="w-3 h-3" /> Portail actif</span>
    : <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400" data-testid="client-account-billing">Sans compte</span>;
}

function ClientFiche({ api, clientId, onEdit, onChanged, onOpenDoc }) {
  const [data, setData] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [invite, setInvite] = useState(null);

  const load = async () => {
    const r = await api.get(`/facturation/clients/${clientId}`);
    setData(r.data);
  };
  useEffect(() => { setInvite(null); load(); }, [clientId]);

  const doInvite = async () => {
    if (!window.confirm("Activer l'accès portail pour ce client ? Un mot de passe provisoire sera généré.")) return;
    setInviting(true);
    try {
      const r = await api.post(`/facturation/clients/${clientId}/invite`);
      setInvite(r.data);
      toast.success(r.data.email_sent ? "Invitation envoyée par e-mail" : "Accès activé — copiez le mot de passe provisoire");
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'activation"); }
    setInviting(false);
  };

  if (!data) return <div className="p-6 text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>;
  const s = data.stats || {};
  return (
    <div className="space-y-4" data-testid="client-fiche">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-bold">{data.company_name || data.name}</h3>
          <div className="text-xs text-slate-400 space-y-0.5 mt-1">
            {data.contact_name && <div>{data.contact_name}</div>}
            {data.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {data.email}</div>}
            {data.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {data.phone}</div>}
            {(data.address || data.city) && <div>{[data.address, data.postal_code, data.city].filter(Boolean).join(" ")}</div>}
            {data.siret && <div>SIRET {data.siret}{data.tva_intra ? ` · TVA ${data.tva_intra}` : ""}</div>}
            {data.payment_terms && <div>Conditions : {data.payment_terms}</div>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <AccountBadge status={data.account_status} />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onEdit(data)} className="border-white/10 text-white h-8 text-xs" data-testid="client-edit-btn"><Pencil className="w-3 h-3 mr-1" /> Modifier</Button>
            {data.account_status !== "active" && (
              <Button size="sm" onClick={doInvite} disabled={inviting} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] h-8 text-xs font-bold" data-testid="client-invite-btn">
                {inviting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <KeyRound className="w-3 h-3 mr-1" />} Activer l'accès portail
              </Button>
            )}
          </div>
        </div>
      </div>

      {invite && !invite.email_sent && invite.temp_password && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs" data-testid="client-invite-result">
          <div className="text-amber-300 font-bold mb-1">E-mail non envoyé (SMTP non configuré) — transmettez ces identifiants :</div>
          <div className="text-slate-300">Identifiant : <b>{invite.email}</b></div>
          <div className="flex items-center gap-2 text-slate-300">Mot de passe provisoire : <code className="bg-black/30 px-1.5 py-0.5 rounded">{invite.temp_password}</code>
            <button onClick={() => { navigator.clipboard?.writeText(invite.temp_password); toast.success("Copié"); }} className="text-[#f7c935]" data-testid="copy-temp-password"><Copy className="w-3 h-3" /></button>
          </div>
        </div>
      )}
      {invite && invite.email_sent && <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-2 text-xs text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Invitation envoyée à {invite.email}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[["CA facturé TTC", `${fmt(s.ca_ttc)} €`, "#f7c935"], ["Encours", `${fmt(s.encours)} €`, s.encours > 0 ? "#ef4444" : "#22c55e"], ["Factures", s.factures || 0, "#e2e8f0"], ["Devis", s.devis || 0, "#3b82f6"]].map(([l, v, c]) => (
          <div key={l} className="bg-[#161020] rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">{l}</div><div className="text-base font-bold" style={{ color: c }}>{v}</div></div>
        ))}
      </div>

      <div>
        <div className="text-xs font-bold text-slate-400 mb-2">Historique ({data.documents?.length || 0})</div>
        {!data.documents?.length ? <div className="text-xs text-slate-600">Aucun document</div> : (
          <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="client-history">
            {data.documents.map(d => (
              <button key={d.invoice_id} onClick={() => onOpenDoc?.(d)} className="w-full flex items-center gap-3 text-xs p-2 rounded-lg bg-[#161020] hover:bg-white/5 text-left" data-testid={`client-doc-${d.invoice_id}`}>
                <span className="font-bold text-slate-200 w-32 truncate">{d.reference}</span>
                <span className="text-slate-500 capitalize">{d.type}{d.kind && d.kind !== "standard" ? ` · ${d.kind}` : ""}</span>
                <span className="text-slate-500">{STATUS_LABEL[d.status] || d.status}</span>
                <span className="ml-auto font-bold">{fmt(d.total_ttc)} €</span>
                <span className="text-slate-600 w-20 text-right">{d.issue_date}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ClientsTab({ api, onOpenDoc, onClientsChanged }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/facturation/clients"); setClients(r.data || []); } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = clients.filter(c => !q || `${c.company_name} ${c.name} ${c.email} ${c.contact_name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4" data-testid="clients-tab">
      <div>
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un client…" className="pl-9 bg-[#161020] border-white/10 text-white h-9" data-testid="clients-search" />
          </div>
          <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold h-9 text-xs" data-testid="clients-new-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau client</Button>
        </div>
        {loading ? <div className="text-slate-500 text-sm p-4">Chargement…</div> : filtered.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm"><Building2 className="w-10 h-10 mx-auto mb-2 text-slate-600" />Aucun client pour cette société</div>
        ) : (
          <div className="space-y-1.5 max-h-[70vh] overflow-y-auto pr-1" data-testid="clients-list">
            {filtered.map(c => (
              <button key={c.user_id} onClick={() => setSelected(c.user_id)} className={`w-full text-left bg-[#14101c] border rounded-xl p-3 flex items-center gap-3 transition-colors ${selected === c.user_id ? "border-[#f7c935]/50" : "border-white/5 hover:border-white/15"}`} data-testid={`client-row-${c.user_id}`}>
                <div className="w-9 h-9 rounded-lg bg-[#f7c935]/10 text-[#f7c935] flex items-center justify-center font-bold text-xs">{(c.company_name || c.name || "?").slice(0, 2).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-slate-200 truncate">{c.company_name || c.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{c.contact_name || c.email || "—"}</div>
                </div>
                <div className="text-right">
                  <div className={`text-xs font-bold ${c.stats?.encours > 0 ? "text-red-400" : "text-slate-400"}`}>{fmt(c.stats?.encours)} €</div>
                  <div className="text-[10px] text-slate-600">{c.stats?.documents || 0} doc.</div>
                </div>
                <AccountBadge status={c.account_status} />
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 min-h-[200px]">
        {selected ? (
          <ClientFiche api={api} clientId={selected} onEdit={(c) => { setEditing(c); setShowForm(true); }} onChanged={() => { load(); onClientsChanged?.(); }} onOpenDoc={onOpenDoc} />
        ) : <div className="h-full flex items-center justify-center text-slate-600 text-sm">Sélectionnez un client pour voir sa fiche, son encours et son historique</div>}
      </div>
      <ClientFormDialog open={showForm} onClose={() => setShowForm(false)} api={api} client={editing} onSaved={(c) => { load(); onClientsChanged?.(); if (c?.user_id) setSelected(c.user_id); }} />
    </div>
  );
}
